
import { AppSettings, Table, MenuItem, Order, Reservation } from '../types';

const getAuthHeaders = () => {
  const token = localStorage.getItem('spacesimple_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const db = {
  // Client-side password persistence for PWA convenience
  saveLocalPassword(password: string) {
    localStorage.setItem('spacesimple_remembered_pass', btoa(password));
  },
  
  getLocalPassword() {
    const saved = localStorage.getItem('spacesimple_remembered_pass');
    try {
      return saved ? atob(saved) : '';
    } catch {
      return '';
    }
  },
  
  clearLocalPassword() {
    localStorage.removeItem('spacesimple_remembered_pass');
  },

  async login(password: string) {
    const res = await fetch('/api/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'ログインに失敗しました');
    }
    
    const data = await res.json();
    localStorage.setItem('spacesimple_token', data.token);
    return data;
  },

  async resetPassword() {
    const res = await fetch('/api/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    return res.json();
  },

  async changePassword(newPassword: string) {
    const res = await fetch('/api/change-password', {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('パスワードの変更に失敗しました');
    return res.json();
  },

  async getAllData() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

    try {
      const res = await fetch('/api/all', { 
        headers: getAuthHeaders(),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (res.status === 401) {
        localStorage.removeItem('spacesimple_token');
        throw new Error('AUTH_REQUIRED');
      }
      
      if (!res.ok) {
        const errorText = await res.text();
        if (errorText.includes('no such table')) {
          throw new Error('DATABASE_NOT_INITIALIZED');
        }
        throw new Error(`SERVER_ERROR: ${res.status}`);
      }
      
      return res.json();
    } catch (e: any) {
      clearTimeout(timeoutId);
      if (e.name === 'AbortError') throw new Error('TIMEOUT');
      throw e;
    }
  },

  async saveSettings(data: AppSettings) {
    return fetch('/api/save', {
      method: 'POST',
      body: JSON.stringify({ type: 'settings', id: 'main', data }),
      headers: getAuthHeaders()
    });
  },

  async saveTable(table: Table) {
    return fetch('/api/save', {
      method: 'POST',
      body: JSON.stringify({ type: 'tables', id: table.id, data: table }),
      headers: getAuthHeaders()
    });
  },

  async deleteTable(id: string) {
    return fetch('/api/delete', {
      method: 'POST',
      body: JSON.stringify({ type: 'delete_item', table: 'tables', itemId: id }),
      headers: getAuthHeaders()
    });
  },

  async saveMenuItem(item: MenuItem) {
    return fetch('/api/save', {
      method: 'POST',
      body: JSON.stringify({ type: 'menu_items', id: item.id, data: item }),
      headers: getAuthHeaders()
    });
  },

  async deleteMenuItem(id: string) {
    return fetch('/api/delete', {
      method: 'POST',
      body: JSON.stringify({ type: 'delete_item', table: 'menu_items', itemId: id }),
      headers: getAuthHeaders()
    });
  },

  async saveOrder(order: Order) {
    return fetch('/api/save', {
      method: 'POST',
      body: JSON.stringify({ type: 'orders', id: order.id, data: order }),
      headers: getAuthHeaders()
    });
  },

  async saveReservation(res: Reservation) {
    return fetch('/api/save', {
      method: 'POST',
      body: JSON.stringify({ type: 'reservations', id: res.id, data: res }),
      headers: getAuthHeaders()
    });
  }
};
